/**
 * PanoramaConfigLoader - Loads panorama positions from the calibration database API
 * Supports dynamic tour loading via URL parameters
 */

import { API_URL } from '../config/env.js';

export class PanoramaConfigLoader {
  constructor(tourId = null, theme = null) {
    this.tourId = tourId
    this.theme = theme
    this.positions = null
    this.modelInfo = null
    this.tourConfig = null
  }

  /**
   * Load panorama positions from the database API or dynamic tour API
   * @returns {Promise<Object>} Object containing panoramas and modelInfo
   */
  async load() {
    // If tourId is provided, use the dynamic tour API
    if (this.tourId) {
      return this.loadDynamicTour()
    }

    // Otherwise, use the legacy database API
    return this.loadLegacy()
  }

  /**
   * Load tour configuration dynamically from the tour API
   * @param {string} viewType - 'model-view' or 'walkthrough-view'
   * @returns {Promise<Object>} Object containing panoramas and modelInfo
   */
  async loadDynamicTour(viewType = 'walkthrough-view') {
    try {
      console.log(`🎯 Loading dynamic tour: ${this.tourId} (${viewType})`)

      // Build URL with optional theme parameter
      let url = `${API_URL}/virtual-tour/tours/${this.tourId}/${viewType}`
      if (this.theme) {
        url += `?theme=${encodeURIComponent(this.theme)}`
        console.log(`🎨 Requesting theme: ${this.theme}`)
      }

      const response = await fetch(url)

      if (!response.ok) {
        // Try to get error details from response
        let errorData
        try {
          errorData = await response.json()
        } catch (e) {
          errorData = null
        }

        if (response.status === 404) {
          const message =
            errorData?.message || errorData?.error || `Tour "${this.tourId}" not found`
          // Create error with 404 flag for proper handling
          const error = new Error(message)
          error.status = 404
          error.is404 = true
          throw error
        }
        throw new Error(`Failed to load tour: ${response.status}`)
      }

      const tourConfig = await response.json()
      console.log("✅ Loaded tour configuration:", tourConfig)

      this.tourConfig = tourConfig

      // Transform tour API format to application format
      const panoramas = {}

      tourConfig.panoramas.forEach((pano) => {
        panoramas[pano.id] = {
          name: pano.title || pano.name,
          position: pano.position,
          rotation: pano.rotation,
          panoRotY: pano.panoRotY,
          landmarks: [],
          connections: pano.connections || [],
          validated: true,
          assetPath: pano.assetPath,
          hasOverlays: pano.hasOverlays,
          thumbnail: pano.thumbnail,
          panoramaPaths: pano.panoramaPaths, // Support custom panorama paths from cloud storage
        }
      })

      this.positions = panoramas

      // Use model info from tour config
      this.modelInfo = {
        file: tourConfig.assets.baseUrl + tourConfig.model.file,
        bounds: tourConfig.model.bounds,
        center: tourConfig.model.center,
        rotation: tourConfig.model.rotation,
        scale: tourConfig.model.scale,
      }

      // Auto-calculate connections if not provided or empty
      const needsConnectionCalculation = Object.values(panoramas).some(
        (pano) => !pano.connections || pano.connections.length === 0,
      )

      if (needsConnectionCalculation) {
        console.log(
          "🔗 Connections missing or empty, auto-calculating based on proximity...",
        )
        this.calculateConnections()
      }

      console.log(
        `✅ Loaded ${Object.keys(panoramas).length} panoramas from tour ${
          this.tourId
        }`,
      )

      return {
        panoramas: this.positions,
        modelInfo: this.modelInfo,
        defaultCameraHeight: 0,
        tourConfig: tourConfig,
      }
    } catch (error) {
      console.error("❌ Failed to load dynamic tour:", error)
      throw error
    }
  }

  /**
   * Load panorama positions from legacy database API
   * @returns {Promise<Object>} Object containing panoramas and modelInfo
   */
  async loadLegacy() {
    try {
      console.log("🔄 Fetching panorama positions from database...")

      const response = await fetch(`${API_URL}/positions`)

      if (!response.ok) {
        throw new Error(`Failed to fetch positions: ${response.status}`)
      }

      const data = await response.json()
      console.log("✅ Loaded panorama positions from database:", data)

      // Transform database format to application format
      const panoramas = {}

      // Handle both array and object formats
      const dataEntries = Array.isArray(data)
        ? data.map((item) => [item.pano_id, item])
        : Object.entries(data)

      dataEntries.forEach(([panoId, pano]) => {
        panoramas[panoId] = {
          name: `Panorama ${panoId}`,
          position: {
            x: pano.x,
            y: pano.y,
            z: pano.z,
          },
          rotation: {
            y: pano.rotY || 0, // Note: API uses 'rotY', not 'rot_y'
          },
          panoRotY: pano.panoRotY || 0, // Note: API uses 'panoRotY', not 'pano_rot_y'
          landmarks: pano.landmarks || [],
          connections: [], // Will be auto-calculated based on proximity
          validated: true, // If it's in the database, it's been calibrated
          note: `Calibrated from database`,
        }
      })

      this.positions = panoramas

      // Use default model info (could be stored in database later)
      this.modelInfo = {
        file: "assets/models/output_00.gltf",
        bounds: {
          x: { min: -1.395, max: 1.12 },
          y: { min: -3.407, max: 2.448 },
          z: { min: -1.206, max: 0.971 },
        },
        center: { x: -0.1375, y: -0.4795, z: -0.1176 },
      }

      // Auto-calculate connections based on proximity
      this.calculateConnections()

      console.log(
        `✅ Loaded ${Object.keys(panoramas).length} calibrated panoramas`,
      )
      console.log("Panorama IDs:", Object.keys(panoramas))

      return {
        panoramas: this.positions,
        modelInfo: this.modelInfo,
        defaultCameraHeight: 0,
      }
    } catch (error) {
      console.error(
        "❌ Failed to load panorama positions from database:",
        error,
      )
      console.warn("⚠️ Falling back to empty panorama set")

      // Return empty config if database is unavailable
      return {
        panoramas: {},
        modelInfo: {
          file: "assets/models/output_00.gltf",
          bounds: {
            x: { min: -1.395, max: 1.12 },
            y: { min: -3.407, max: 2.448 },
            z: { min: -1.206, max: 0.971 },
          },
          center: { x: -0.1375, y: -0.4795, z: -0.1176 },
        },
        defaultCameraHeight: 0,
      }
    }
  }

  /**
   * Auto-calculate connections between panoramas based on proximity
   * Connects panoramas that are within 3 units of each other
   */
  calculateConnections() {
    const maxDistance = 3.0 // Maximum distance to consider as "connected"
    const panoIds = Object.keys(this.positions)

    panoIds.forEach((id1) => {
      const pano1 = this.positions[id1]
      const connections = []

      panoIds.forEach((id2) => {
        if (id1 === id2) return

        const pano2 = this.positions[id2]
        const distance = Math.sqrt(
          Math.pow(pano1.position.x - pano2.position.x, 2) +
            Math.pow(pano1.position.y - pano2.position.y, 2) +
            Math.pow(pano1.position.z - pano2.position.z, 2),
        )

        if (distance <= maxDistance) {
          connections.push(id2)
        }
      })

      pano1.connections = connections
      console.log(
        `📍 Panorama ${id1} connected to: [${connections.join(", ")}]`,
      )
    })
  }

  /**
   * Get all panorama positions
   */
  getPanoramas() {
    return this.positions
  }

  /**
   * Get a specific panorama by ID
   */
  getPanorama(id) {
    return this.positions[id]
  }

  /**
   * Get model info
   */
  getModelInfo() {
    return this.modelInfo
  }
}
